import { useState, forwardRef, useImperativeHandle } from 'react';
import {
	StatusToastItem,
	StatusToastWrapperContent,
	StatusToastWrapperContentText,
	StatusToastTextHeadline,
	StatusToastTextMessage,
	StyledIcon,
	ReplyToastIconWrapper,
	ReplyToastActions,
} from './StatusToast.style';
import { Button } from '../button/Button';
import { ToastData } from './StatusToastContext';

interface ReplyToastProps {
	toast: ToastData;
	onHide: () => void;
}

export interface ReplyToastHandle {
	hide: () => void;
}

const ANIMATION_DURATION_MS = 300;

export const ReplyToast = forwardRef<ReplyToastHandle, ReplyToastProps>(({
	toast,
	onHide,
}, ref) => {
	const [isExiting, setIsExiting] = useState(false);

	const handleHide = () => {
		setIsExiting(true);
		setTimeout(() => {
			onHide();
		}, ANIMATION_DURATION_MS);
	};

	useImperativeHandle(ref, () => ({
		hide: handleHide,
	}));

	const handleReply = () => {
		toast.onReply?.();
		handleHide();
	};

	const handleConfirm = () => {
		toast.onConfirm?.();
		handleHide();
	};

	return (
		<StatusToastItem $isExiting={isExiting}>
			<StatusToastWrapperContent>
				<ReplyToastIconWrapper aria-hidden="true">
					<StyledIcon icon="sparkles-regular" $variant="info" />
				</ReplyToastIconWrapper>
				<StatusToastWrapperContentText>
					<StatusToastTextHeadline>{toast.headline}</StatusToastTextHeadline>
					<StatusToastTextMessage>{toast.message}</StatusToastTextMessage>
				</StatusToastWrapperContentText>
				<StyledIcon
					$variant="close"
					icon="x-slim"
					onClick={handleHide}
					style={{ cursor: 'pointer' }}
				/>
			</StatusToastWrapperContent>
			<ReplyToastActions>
				<Button variant="default" onClick={handleReply}>
					{toast.replyLabel ?? 'Reply'}
				</Button>
				<Button variant="primary" onClick={handleConfirm}>
					{toast.confirmLabel ?? 'OK'}
				</Button>
			</ReplyToastActions>
		</StatusToastItem>
	);
});

ReplyToast.displayName = 'ReplyToast';
